/*!
 * Copyright © 2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import {
  IVehiclePlot,
  IClusterBalloonItem,
  IPoint,
  IMapContextMenuData,
  MarkerInfo,
  IAssetPlot,
  LatLng,
  VehicleMarkerInfo,
  IMarkerOptions,
  AssetMarkerInfo,
  EPlotType } from '../../../core/models';
import { DomHelperService } from '../../../core/services';
import { IContextMenuDataLoaderservice } from './context-menu-data-loaderservice.interface';

@Injectable({
  providedIn: 'root'
})
export class ContextMenuDataLoaderService implements IContextMenuDataLoaderservice {

  constructor(private readonly _domHelperService: DomHelperService) { }

  public getVehicleContextMenuData(vehicle: IVehiclePlot | IClusterBalloonItem, clickPosition: IPoint): IMapContextMenuData<MarkerInfo> {
    const vehicleLatLng = new LatLng(
      this._isClusterBalloonItem(vehicle) ? vehicle.latLng.lat : vehicle.coordinates.lat,
      this._isClusterBalloonItem(vehicle) ? vehicle.latLng.lng : vehicle.coordinates.lng
    );

    this._getClickPosition(clickPosition);

    const vehicleMarkerInfo: VehicleMarkerInfo = new VehicleMarkerInfo(
      vehicle.id,
      vehicleLatLng,
      this._isClusterBalloonItem(vehicle) ? vehicle.label : vehicle.vehicleName,
      vehicle.type,
      vehicle.status,
      vehicle.driverId,
      vehicle.hasNavigationDevice,
      { iconName: vehicle.iconClass } as IMarkerOptions
    );

    const vehicleContextMenuData: IMapContextMenuData<MarkerInfo> = {
      clickPosition,
      clickLatLng: vehicleLatLng,
      streetViewAvailable: false,
      markerInfo: vehicleMarkerInfo
    };

    return vehicleContextMenuData;
  }

  public getAssetContextMenuData(asset: IClusterBalloonItem | IAssetPlot, clickPosition: IPoint): IMapContextMenuData<MarkerInfo> {
    const assetLatLng = new LatLng(
      this._isClusterBalloonItem(asset) ? asset.latLng.lat : asset.center.Latitude,
      this._isClusterBalloonItem(asset) ? asset.latLng.lng : asset.center.Longitude
    );

    this._getClickPosition(clickPosition);

    const assetMarkerInfo: AssetMarkerInfo = new AssetMarkerInfo(
      asset.id, assetLatLng,
      this._isClusterBalloonItem(asset) ? asset.label : asset.name,
      EPlotType.asset,
      { iconName: asset.iconClass } as IMarkerOptions);

      const assetContextMenuData: IMapContextMenuData<MarkerInfo> = {
      clickPosition,
      clickLatLng: assetLatLng,
      streetViewAvailable: false,
      markerInfo: assetMarkerInfo
    };

    return assetContextMenuData;
  }

  private _isClusterBalloonItem(item: IVehiclePlot | IAssetPlot | IClusterBalloonItem): item is IClusterBalloonItem {
    return (<IClusterBalloonItem>item).latLng !== undefined;
  }

  private _getClickPosition(clickPosition: IPoint): void {
    const mapBoundingRect = this._domHelperService.getBoundingClientRectWithSelector('div.fm-map-container');
    clickPosition.y = clickPosition.y - mapBoundingRect.top;
  }
}
